import React, { useState, useEffect, useRef } from 'react';
import { Button } from './button';
import { Input } from './input';
import { cn } from '@/lib/utils';
import { Highlighter } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';

interface QuickSelectInputProps {
  value?: string;
  onChange?: (value: string) => void;
  presetValues: Array<{ value: string; label: string }>;
  placeholder?: string;
  unit?: string;
  className?: string;
  inputType?: 'text' | 'number';
  fieldName?: string;
  form?: UseFormReturn<any>;
}

export function QuickSelectInput({
  value,
  onChange,
  presetValues,
  placeholder = 'Enter custom value',
  unit,
  className,
  inputType = 'number',
  fieldName,
  form,
}: QuickSelectInputProps) {
  const [isHighlighted, setIsHighlighted] = useState(false);
  
  // Use a ref to track current state for comparison in subscription callback
  const isHighlightedRef = useRef(isHighlighted);
  useEffect(() => {
    isHighlightedRef.current = isHighlighted;
  }, [isHighlighted]);

  // Helper to strip HTML marks from value for comparison
  const stripMarks = (val: string | undefined): string => {
    if (!val) return '';
    return val.replace(/<\/?mark[^>]*>/g, '');
  };

  // Get clean value for display and comparison
  const cleanValue = stripMarks(value);

  // Subscribe to form changes properly using form.watch() with callback
  useEffect(() => {
    if (!fieldName || !form) return;

    // Function to sync highlight state from form
    const syncFromForm = () => {
      // Always read fresh from form.getValues() to ensure we get latest data
      const highlightedFields = form.getValues('highlightedFields') || {};
      const formHighlightState = !!highlightedFields[fieldName];
      if (formHighlightState !== isHighlightedRef.current) {
        setIsHighlighted(formHighlightState);
      }
    };

    // Initial sync
    syncFromForm();

    // Subscribe to all form changes - callback fires on any form value change
    const subscription = form.watch((data, { name }) => {
      // Sync on full form update (name is undefined, e.g., from form.reset())
      // or when highlightedFields specifically changes
      if (!name || name === 'highlightedFields' || name.startsWith('highlightedFields.')) {
        syncFromForm();
      }
    });

    return () => subscription.unsubscribe();
  }, [fieldName, form]);

  const handlePresetClick = (presetValue: string) => {
    onChange?.(presetValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  const toggleHighlight = () => {
    if (!fieldName || !form) return;

    const currentHighlights = form.getValues('highlightedFields') || {};
    const newHighlighted = !isHighlighted;

    form.setValue('highlightedFields', {
      ...currentHighlights,
      [fieldName]: newHighlighted,
    });

    setIsHighlighted(newHighlighted);
  };

  return (
    <div className={cn('space-y-3', className)}>
      {/* Quick Select Buttons */}
      <div className="flex flex-wrap gap-2">
        {presetValues.map((preset) => {
          const isSelected = cleanValue === preset.value;
          return (
            <Button
              key={preset.value}
              type="button"
              variant={isSelected ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePresetClick(preset.value)}
              data-testid={`button-preset-${preset.value}`}
              className={cn(
                'transition-all',
                isSelected && 'bg-primary text-primary-foreground shadow-sm'
              )}
            >
              {preset.label}
            </Button>
          );
        })}
      </div>

      {/* Custom Value Input with Highlight Button */}
      <div className="space-y-1.5">
        <div className="text-xs text-muted-foreground">Or enter custom value:</div>
        <div className="relative">
          <Input
            type={inputType}
            value={cleanValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            step={inputType === 'number' ? '0.01' : undefined}
            min={inputType === 'number' ? '0' : undefined}
            data-testid="input-export-limitation"
            className={cn(
              isHighlighted ? 'pr-16' : 'pr-12',
              isHighlighted && 'border-yellow-400 bg-yellow-50/50 dark:bg-yellow-950/20'
            )}
          />
          {unit && (
            <div className={cn(
              "absolute top-1/2 -translate-y-1/2 pointer-events-none text-sm text-muted-foreground font-medium",
              isHighlighted ? "right-9" : "right-3"
            )}>
              {unit}
            </div>
          )}
          {isHighlighted && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <Highlighter className="h-4 w-4 text-yellow-600" />
            </div>
          )}
        </div>
        {fieldName && form && (
          <div className="flex items-center justify-between">
            <Button
              type="button"
              size="sm"
              variant={isHighlighted ? 'default' : 'outline'}
              onClick={toggleHighlight}
              onMouseDown={(e) => e.preventDefault()}
              className={cn(
                'text-xs',
                isHighlighted && 'bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700'
              )}
            >
              <Highlighter className="h-3 w-3 mr-1" />
              {isHighlighted ? 'Highlighted' : 'Highlight'}
            </Button>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              {isHighlighted && <Highlighter className="h-3 w-3 text-yellow-600" />}
              {isHighlighted ? 'Will appear in PDF' : 'Click to mark for PDF'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
